import { BookOpen, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const RULES = [
  { rule: "Contacto", desc: "Contacto realizado com potencial cliente · reportado pelo consultor", pts: "+1" },
  { rule: "Reunião realizada", desc: "Reunião presencial ou online concretizada · reportada pelo consultor", pts: "+15" },
  { rule: "Angariação", desc: "Imóvel angariado e publicado · reportada pelo consultor", pts: "+20" },
  { rule: "Venda", desc: "Negócio fechado / escritura · reportada pelo consultor", pts: "+50" },
  { rule: "Post redes sociais", desc: "Publicação em LinkedIn, Instagram, Facebook ou TikTok · reportada pelo consultor (URL opcional como prova)", pts: "+5" },
  { rule: "Vídeo humanizado", desc: "Vídeo curto com cara e voz do consultor (LinkedIn, Instagram, YouTube, TikTok) · reportada pelo consultor (URL opcional como prova)", pts: "+10" },
  { rule: "Formação", desc: "Participação em formação (interna KW, externa, workshop, curso online, KWU…) · reportada pelo consultor por sessão frequentada", pts: "+8" },
  { rule: "Convite aceite / Recrutamento (Padrinho)", desc: "Convidas um novo consultor para a Liga via link mágico/Onboarding; quando ele faz o 1.º reporting, recebes este bónus. Aplica-se também quando o admin define 'Recomendado por'.", pts: "+20" },
  { rule: "Padrinho Dourado", desc: "Bónus de prestígio: quando tens 3+ afilhados ativos (não-excluídos) na liga, ficas elegível. Os pontos só entram depois de autorização explícita do admin.", pts: "+50" },
  { rule: "Growth Share externo — recrutador", desc: "Novo consultor que entra para a KW Lead (não precisa ser consultor da liga). Atribuído automaticamente quando o admin define o 'Recomendado por' do novo consultor + tipo 'Externo'.", pts: "+100" },
  { rule: "Growth Share interno — recrutador", desc: "Consultor KW que entra para a KW Lead Growth League. Atribuído automaticamente ao recrutador quando o admin/coach define 'Recomendado por' + tipo 'Interno'.", pts: "+20" },
  { rule: "Growth Share — equipa", desc: "Bónus coletivo: por cada novo consultor KW Lead (GS externo) trazido por um colega de equipa, os restantes membros ganham", pts: "+20" },
  { rule: "Growth Share — clube", desc: "Bónus coletivo: por cada novo consultor KW Lead (GS externo) trazido por um colega do mesmo clube MBTI, os restantes membros do clube ganham (cumula com o bónus de equipa)", pts: "+20" },
  { rule: "Reflexão pós-formação", desc: "Sempre que escreves uma reflexão (mínimo 50 caracteres) numa das sessões de formação do teu clube. Pode ser editada; +3 pts por sessão (uma vez).", pts: "+3" },
  { rule: "Saldo Presença inicial", desc: "Todos começam com 100 pontos de presença. À 3ª falta passa para 0.", pts: "100" },
  { rule: "Presença total semanal", desc: "Se todos da equipa estiverem presentes na reunião semanal", pts: "+5" },
  { rule: "Pontualidade", desc: "Chegar a horas à reunião · marcado pelo Secretário ou Capitão da semana", pts: "+5" },
  { rule: "Reportou", desc: "Reporting semanal submetido até Domingo 23:59 · auto-atribuído pelo sistema", pts: "+5" },
  { rule: "TPC realizado", desc: "Tarefa semanal concluída · marcada pelo Secretário ou Capitão da semana", pts: "+5" },
  { rule: "Bónus consistência reporting", desc: "Reportou nas 10 semanas", pts: "+100" },
  { rule: "Penalização — Não Reportou", desc: "Reporting não submetido até Domingo 23:59 da semana respetiva", pts: "-10", danger: true },
];

const COACH_RULES = [
  { t: "Secretário da Semana", d: "Premiado todas as semanas. Critério combinado: 40% média de pontos/semana do clube + 30% crescimento vs semana anterior + 30% % de membros com presença 100% e reporting completo. Vence o secretário com o score mais alto." },
  { t: "Secretário do Mês", d: "Recálculo mensal pelo mesmo critério combinado (sobre todas as semanas do mês), independente das semanas individuais ganhas." },
  { t: "Reconhecimento", d: "Badge \"Secretário da Semana\" 🏆 no perfil do secretário + destaque no Dashboard. Sem pontos (os secretários não coopetem no ranking de consultores)." },
  { t: "MBTI do Secretário", d: "Cada secretário tem o seu próprio perfil MBTI (16Personalities), independente do clube que coordena. O clube é atribuído pelo Presidente da Liga / Admin." },
];

const ELIM = [
  { t: "Elegibilidade MVP", d: "Máximo de 3 faltas e consultor ativo. À 4.ª falta deixa de ser elegível para MVP." },
  { t: "Exclusão por presença", d: "À 5.ª falta, o consultor sai da edição atual." },
  { t: "Exclusão por reporting", d: "À 3.ª vez sem reportar, o consultor sai da edição atual." },
  { t: "Critério MVP", d: "Entre elegíveis: maior nº de vendas; depois nº de reportings 'Reportou'." },
  { t: "Capitão semanal", d: "O coach nomeia 1 consultor por semana que pode validar presença/pontualidade/TPC dos colegas do clube." },
  { t: "Ranking principal", d: "Classificação por pontos totais. Desempate por Taxa de Conversão Ponderada (20% R/C + 60% A/R + 20% V/A) e depois por vendas." },
];

export default function Rules() {
  const { user } = useAuth();
  const showCoachRules = user?.role === "coach" || user?.role === "admin";
  return (
    <div className="space-y-10 kw-fade-up" data-testid="rules-page">
      <div className="flex items-end gap-4">
        <BookOpen className="w-10 h-10 text-[#E50000] mb-1" />
        <div>
          <div className="kw-overline mb-2">Documento oficial</div>
          <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter">
            Regras <span className="text-[#E50000]">Oficiais</span>
          </h1>
        </div>
      </div>

      <div className="kw-card p-6">
        <p className="text-[#A3A3A3] max-w-3xl">
          <span className="text-white font-display uppercase tracking-wider font-bold">Regra de Ouro:</span>{" "}
          Nenhuma ação sem compreensão — nenhum resultado sem relação. Coopetimos para crescer, não para ganhar a qualquer custo.
        </p>
      </div>

      <div className="kw-card overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#0A0A0A] border-b border-[#222]">
            <tr className="kw-overline">
              <th className="p-4">Regra</th>
              <th className="p-4">Descrição</th>
              <th className="p-4 text-right">Pontos</th>
            </tr>
          </thead>
          <tbody>
            {RULES.map((r) => (
              <tr key={r.rule} className="border-b border-[#1A1A1A] hover:bg-[#141414]">
                <td className="p-4 font-display font-bold uppercase tracking-tight">{r.rule}</td>
                <td className="p-4 text-[#A3A3A3]">{r.desc}</td>
                <td className={`p-4 text-right font-display text-xl font-black ${r.danger ? "text-[#FF6B6B]" : "text-[#E50000]"}`}>{r.pts}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {ELIM.map((e) => (
          <div key={e.t} className="kw-card p-5 kw-card-hover">
            <div className="kw-overline mb-2">Critério</div>
            <div className="font-display text-xl font-black uppercase">{e.t}</div>
            <p className="text-[#A3A3A3] text-sm mt-2">{e.d}</p>
          </div>
        ))}
      </div>

      <div className="space-y-4 pt-6 border-t border-[#222]" data-testid="rules-cards-section">
        <div>
          <div className="kw-overline mb-1">Disciplina e mérito</div>
          <h2 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">
            Os três <span className="text-[#E50000]">cartões</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              c: "#EAB308", t: "Amarelo", s: "Advertência",
              d: "Automático a 2 semanas sem reportar ou 3 faltas às sessões. Pode também ser dado à mão por quebra da Regra de Ouro. Não retira pontos — avisa.",
            },
            {
              c: "#E50000", t: "Vermelho", s: "Expulsão",
              d: "Automático a 3 semanas sem reportar ou 5 faltas. Sai da coopetição e deixa de contar para o clube.",
            },
            {
              c: "#FFFFFF", t: "Branco", s: "Iniciativa que aporta valor",
              d: "Para quem faz a Liga crescer — por exemplo uma sugestão EUREKA implementada. Vale 10 pontos a cada um dos 8 que coopetem nesse clube, ou seja 80 pontos ao clube.",
            },
          ].map((x) => (
            <div key={x.t} className="kw-card p-5" style={{ borderTop: `3px solid ${x.c}` }}
              data-testid={`rule-card-${x.t}`}>
              <div className="w-5 h-7 rounded-[2px] mb-3" style={{ background: x.c }} />
              <div className="font-display text-2xl font-black uppercase tracking-tight" style={{ color: x.c }}>
                {x.t}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">{x.s}</div>
              <p className="text-sm text-[#A3A3A3] leading-relaxed mt-3">{x.d}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-4 pt-6 border-t border-[#222]" data-testid="rules-leadership-section">
        <div>
          <div className="kw-overline mb-1">Escolhidos pelo plantel · não coopetem</div>
          <h2 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">
            Os cargos da Liga e dos clubes
          </h2>
        </div>
        <p className="text-sm text-[#A3A3A3] max-w-3xl leading-relaxed">
          São consultores como os outros — continuam a registar a sua actividade e a ter o seu Ranking
          Accountability — mas <strong className="text-white">nenhum deles entra na coopetição</strong>:
          ficam fora dos 8 e os seus pontos não contam para o campeonato do clube. São escolhidos pela
          experiência, idoneidade e capacidade agregadora.
        </p>
        <div className="grid md:grid-cols-3 gap-4 text-sm text-[#A3A3A3]">
          {[
            ["Consultor Presidente da Liga", "A entidade máxima da Liga, acima dos administradores dos clubes. Um só para toda a KW Lead."],
            ["Secretário do Clube", "Um por clube, de um perfil diferente do clube que acompanha — traz um olhar de fora. É o único dos três que entra no Ranking dos Secretários."],
            ["Presidente do Clube", "Um por clube, do perfil do próprio clube. Zela pela união e pela administração — a cola que mantém o grupo junto."],
          ].map(([t, d]) => (
            <div key={t} className="kw-card p-5">
              <div className="font-display text-lg font-black uppercase tracking-tight text-[#EAB308] mb-2">{t}</div>
              <p className="leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-[#737373]">
          Atribuição: Plantel da Liga → o Presidente da Liga no topo da página; o Secretário e o Presidente do Clube no
          cartão de cada clube, em <strong>Liderança do clube · cargos</strong> → "Definir".
          Cada cargo tem direito a uma <strong>mensagem semanal</strong> ao seu clube (a do Presidente é para toda a Liga),
          visível no Dashboard em "Voz da Liderança".
        </p>
      </div>

      <div className="space-y-4 pt-6 border-t border-[#222]" data-testid="rules-eureka-section">
        <div>
          <div className="kw-overline mb-1">Programa transversal · todos os membros KW Lead</div>
          <h2 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">
            EU<span className="text-[#EAB308]">REKA</span>
          </h2>
        </div>
        <p className="text-sm text-[#A3A3A3] max-w-3xl leading-relaxed">
          O EUREKA vive fora da coopetição: está aberto a todos os membros da KW Lead — a coopetir,
          observadores e staff. Qualquer pessoa submete uma ideia em duas vias: <strong>Melhoria KW Lead</strong>,
          para o desenvolvimento orgânico e sustentável da estrutura, ou <strong>Melhoria Growth League</strong>,
          para as regras e a dinâmica do jogo.
        </p>
        <div className="grid md:grid-cols-3 gap-4 text-sm text-[#A3A3A3]">
          {[
            ["Votação entre pares", "Um voto por pessoa. Com 5 votos a ideia entra automaticamente em análise. A decisão final é do admin, com nota visível a todos."],
            ["Cartão branco", "Ideia implementada vale um cartão branco: 10 pontos a cada um dos 8 que coopetem no clube do autor — 80 ao clube."],
            ["e-ROI e legado", "Ao implementar registamos horas poupadas, € por ano e pessoas beneficiadas. Fica no perfil do autor para sempre — o Eternal Return of Investment."],
          ].map(([t, d]) => (
            <div key={t} className="kw-card p-5">
              <div className="font-display text-lg font-black uppercase tracking-tight text-white mb-2">{t}</div>
              <p className="leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
        <Link to="/eureka" className="kw-btn-secondary inline-flex items-center gap-2" data-testid="rules-eureka-link">
          Ir para o EUREKA
        </Link>
      </div>

        <div className="space-y-4 pt-6 border-t border-[#222]" data-testid="rules-coach-section">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-7 h-7 text-[#EAB308]" />
            <div>
              <div className="kw-overline mb-1 text-[#EAB308]">Apenas Secretário &amp; Admin</div>
              <h2 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">
                Campeonato dos <span className="text-[#EAB308]">Secretários</span>
              </h2>
            </div>
          </div>
          <p className="text-sm text-[#A3A3A3] max-w-3xl">
            Os coaches têm o seu próprio campeonato dentro da Liga. Não coopetem em pontos com os consultores
            — coopetem entre si pelo desempenho coletivo dos clubes que coordenam.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {COACH_RULES.map((r) => (
              <div key={r.t} className="kw-card p-5 kw-card-hover border-[#EAB308]/30">
                <div className="kw-overline mb-2 text-[#EAB308]">Secretário</div>
                <div className="font-display text-xl font-black uppercase">{r.t}</div>
                <p className="text-[#A3A3A3] text-sm mt-2">{r.d}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
